import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGMB } from '../contexts/GMBContext';
import { useLanguage } from '../contexts/LanguageContext';
import { analyticsAPI, postsAPI } from '../lib/api';
import { 
  FileText, MessageSquare, BarChart3, TrendingUp, Eye, MousePointer,
  Star, MapPin, Phone, Globe, Clock, AlertTriangle, CheckCircle,
  Lightbulb, Calendar, ChevronRight, Plus, RefreshCw, ExternalLink, Unlink
} from 'lucide-react';
import { toast } from 'sonner';

const DashboardHome = () => {
  const { user } = useAuth();
  const { gmbProfile, isConnected, connectionMethod, googleEmail, locations, locationError, connectGMB, disconnectGMB, fetchLocations, refreshProfile, loading: gmbLoading } = useGMB();
  const { t } = useLanguage();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchingLocations, setFetchingLocations] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // Handle OAuth callback params
    const gmbConnected = searchParams.get('gmb_connected');
    const gmbError = searchParams.get('gmb_error');
    
    if (gmbConnected === 'true') {
      toast.success('Google Business Profile connected successfully!');
      searchParams.delete('gmb_connected');
      setSearchParams(searchParams, { replace: true });
      // Refresh GMB status to pick up the new OAuth connection
      refreshProfile();
    }
    if (gmbError) {
      toast.error(`GMB connection failed: ${gmbError}`);
      searchParams.delete('gmb_error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refreshProfile]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const usageRes = await analyticsAPI.getUsage();
      setUsage(usageRes.data.usage);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-[#1E8E3E]';
    if (score >= 60) return 'text-[#F9AB00]';
    return 'text-[#D93025]';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-[#E6F4EA]';
    if (score >= 60) return 'bg-[#FEF7E0]';
    return 'bg-[#FCE8E6]';
  };

  const planLimits = {
    free: { month: 30 },
    basic: { month: 100 },
    pro: { month: 9999 }
  };

  const currentPlanLimits = planLimits[user?.membership_plan] || planLimits.free;

  if (loading || gmbLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[#1A73E8] animate-spin" />
      </div>
    );
  }

  // If GMB not connected, show connection prompt
  if (!isConnected) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <div className="dashboard-card text-center py-12">
          <div className="w-20 h-20 mx-auto bg-[#E8F0FE] rounded-full flex items-center justify-center mb-6">
            <MapPin className="w-10 h-10 text-[#1A73E8]" />
          </div>
          <h2 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
            Connect Your Google Business Profile
          </h2>
          <p className="mt-2 text-[#5F6368] max-w-md mx-auto">
            Link your GMB profile to unlock AI-powered posts, review management, and optimization insights.
          </p>
          <button
            onClick={connectGMB}
            className="mt-8 inline-flex items-center gap-3 px-8 py-3 bg-white border-2 border-[#DADCE0] rounded-lg hover:bg-[#F8F9FA] transition-colors text-[#202124] font-medium"
            data-testid="connect-gmb-btn"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Sign in with Google
          </button>
          <p className="mt-4 text-xs text-[#5F6368]">
            Secure OAuth 2.0 connection. We never store your Google password.
          </p>
        </div>
      </div>
    );
  }

  const handleFetchLocations = async () => {
    setFetchingLocations(true);
    await fetchLocations();
    setFetchingLocations(false);
  };

  // OAuth-connected dashboard
  if (connectionMethod === 'oauth') {
    return (
      <div className="space-y-6 animate-fade-in-up">
        {/* Google Connected Profile Card */}
        <div className="dashboard-card">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-16 h-16 rounded-xl bg-[#E8F0FE] flex items-center justify-center border border-[#DADCE0]">
                <svg viewBox="0 0 24 24" className="w-8 h-8">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
                    Google Business Profile
                  </h2>
                  <span className="flex items-center gap-1 text-xs text-[#1E8E3E] bg-[#E6F4EA] px-2 py-1 rounded-full font-medium">
                    <CheckCircle className="w-3 h-3" />
                    Connected
                  </span>
                </div>
                <p className="text-[#5F6368] mt-1 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  {googleEmail}
                </p>
                {locations.length > 0 && (
                  <p className="text-sm text-[#5F6368] mt-1">
                    {locations.length} business location{locations.length !== 1 ? 's' : ''} found
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleFetchLocations}
                disabled={fetchingLocations}
                className="btn-outline text-sm py-2 px-4 flex items-center gap-2"
                data-testid="fetch-locations-btn"
              >
                <RefreshCw className={`w-4 h-4 ${fetchingLocations ? 'animate-spin' : ''}`} />
                {fetchingLocations ? 'Loading...' : 'Fetch Locations'}
              </button>
              <button
                onClick={disconnectGMB}
                className="text-sm text-[#5F6368] hover:text-[#D93025] py-2 px-4 flex items-center gap-2 rounded-lg hover:bg-[#FCE8E6] transition-colors"
                data-testid="disconnect-gmb-btn"
              >
                <Unlink className="w-4 h-4" />
                Disconnect
              </button>
            </div>
          </div>
        </div>

        {/* Locations List */}
        {locations.length > 0 && (
          <div className="dashboard-card">
            <h3 className="text-lg font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>
              Your Business Locations
            </h3>
            <div className="space-y-3">
              {locations.map((loc, i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-[#F8F9FA] rounded-lg">
                  <MapPin className="w-5 h-5 text-[#1A73E8] flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-[#202124]">{loc.name}</p>
                    {loc.category && <p className="text-sm text-[#5F6368]">{loc.category}</p>}
                    {loc.address && <p className="text-xs text-[#5F6368]">{Array.isArray(loc.address) ? loc.address.join(', ') : loc.address}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Location Error / API Not Approved Message */}
        {locationError && (
          <div className="dashboard-card border-l-4 border-[#F9AB00] bg-[#FEF7E0]">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#F9AB00] mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-[#202124]">Unable to fetch business locations</h4>
                <p className="text-sm text-[#5F6368] mt-1">
                  Your Google account is connected, but the Business Profile API may not be approved yet. 
                  Once approved, your locations will appear here automatically.
                </p>
                <p className="text-xs text-[#9AA0A6] mt-2 font-mono">{locationError}</p>
              </div>
            </div>
          </div>
        )}

        {locations.length === 0 && !locationError && (
          <div className="dashboard-card text-center py-8">
            <MapPin className="w-10 h-10 mx-auto text-[#DADCE0]" />
            <p className="mt-3 text-[#5F6368]">
              No locations loaded yet. Click "Fetch Locations" to load your business profiles.
            </p>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="dashboard-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#E8F0FE] rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#1A73E8]" />
              </div>
              <div>
                <p className="text-sm text-[#5F6368]">Posts This Month</p>
                <p className="text-xl font-semibold text-[#202124]">
                  {usage?.month_posts || 0} / {currentPlanLimits.month}
                </p>
              </div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#E6F4EA] rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-[#1E8E3E]" />
              </div>
              <div>
                <p className="text-sm text-[#5F6368]">Replies This Month</p>
                <p className="text-xl font-semibold text-[#202124]">
                  {usage?.month_replies || 0}
                </p>
              </div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FEF7E0] rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#F9AB00]" />
              </div>
              <div>
                <p className="text-sm text-[#5F6368]">Locations</p>
                <p className="text-xl font-semibold text-[#202124]">{locations.length}</p>
              </div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#F3E8FD] rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-[#9334E9]" />
              </div>
              <div>
                <p className="text-sm text-[#5F6368]">Plan</p>
                <p className="text-xl font-semibold text-[#202124] capitalize">{user?.membership_plan || 'free'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link to="/dashboard/post-generator" className="dashboard-card group" data-testid="quick-create-post">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#E8F0FE] rounded-lg flex items-center justify-center group-hover:bg-[#1A73E8] transition-colors">
                <Plus className="w-6 h-6 text-[#1A73E8] group-hover:text-white transition-colors" />
              </div>
              <div>
                <h3 className="font-medium text-[#202124]">Create Post</h3>
                <p className="text-sm text-[#5F6368]">AI-powered</p>
              </div>
            </div>
          </Link>
          <Link to="/dashboard/review-reply" className="dashboard-card group" data-testid="quick-reply">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#E6F4EA] rounded-lg flex items-center justify-center group-hover:bg-[#1E8E3E] transition-colors">
                <MessageSquare className="w-6 h-6 text-[#1E8E3E] group-hover:text-white transition-colors" />
              </div>
              <div>
                <h3 className="font-medium text-[#202124]">Reply Reviews</h3>
                <p className="text-sm text-[#5F6368]">Auto-generate</p>
              </div>
            </div>
          </Link>
          <Link to="/dashboard/audit" className="dashboard-card group" data-testid="quick-audit">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#FEF7E0] rounded-lg flex items-center justify-center group-hover:bg-[#F9AB00] transition-colors">
                <BarChart3 className="w-6 h-6 text-[#F9AB00] group-hover:text-white transition-colors" />
              </div>
              <div>
                <h3 className="font-medium text-[#202124]">Full Audit</h3>
                <p className="text-sm text-[#5F6368]">Optimize profile</p>
              </div>
            </div>
          </Link>
        </div>

        {/* Upgrade CTA for Free Users */}
        {user?.membership_plan === 'free' && (
          <div className="dashboard-card bg-gradient-to-r from-[#1A73E8] to-[#1557B0] text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>
                  Unlock More Features
                </h3>
                <p className="mt-1 text-white/80">
                  Get auto-posting, auto-reply, unlimited posts, and priority support.
                </p>
              </div>
              <Link to="/pricing" className="bg-white text-[#1A73E8] font-semibold px-6 py-3 rounded-lg hover:bg-[#F8F9FA] transition-colors" data-testid="upgrade-cta">
                Upgrade Now
              </Link>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Mock-connected dashboard (shows full mock data)
  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Connected GMB Profile Card */}
      <div className="dashboard-card">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          {/* Profile Info */}
          <div className="flex items-start gap-4 flex-1">
            <img 
              src={gmbProfile.profileImage} 
              alt={gmbProfile.name}
              className="w-20 h-20 rounded-xl object-cover border border-[#DADCE0]"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
                  {gmbProfile.name}
                </h2>
                {gmbProfile.isVerified && (
                  <CheckCircle className="w-5 h-5 text-[#1A73E8]" />
                )}
                <span className="flex items-center gap-1 text-xs text-[#5F6368] bg-[#F1F3F4] px-2 py-1 rounded-full">
                  Demo
                </span>
              </div>
              <p className="text-[#5F6368]">{gmbProfile.category} · {gmbProfile.city}</p>
              <div className="flex items-center gap-4 mt-2 flex-wrap">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-[#F9AB00] text-[#F9AB00]" />
                  <span className="font-medium text-[#202124]">{gmbProfile.rating}</span>
                  <span className="text-[#5F6368]">({gmbProfile.reviewCount} reviews)</span>
                </div>
                <button
                  onClick={disconnectGMB}
                  className="flex items-center gap-1 text-sm text-[#5F6368] hover:text-[#D93025] transition-colors"
                  data-testid="disconnect-gmb-btn"
                >
                  <Unlink className="w-3 h-3" />
                  Disconnect
                </button>
              </div>
            </div>
          </div>

          {/* Audit Score */}
          <div className={`${getScoreBg(gmbProfile.auditScore)} rounded-xl p-6 text-center min-w-[180px]`}>
            <p className="text-sm text-[#5F6368]">Optimization Score</p>
            <p className={`text-4xl font-bold ${getScoreColor(gmbProfile.auditScore)}`} style={{ fontFamily: 'Outfit' }}>
              {gmbProfile.auditScore}
            </p>
            <p className="text-xs text-[#5F6368]">out of 100</p>
            <Link 
              to="/dashboard/audit" 
              className="mt-2 text-sm text-[#1A73E8] font-medium hover:underline inline-flex items-center gap-1"
            >
              View Full Audit <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="dashboard-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#E8F0FE] rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-[#1A73E8]" />
            </div>
            <div>
              <p className="text-sm text-[#5F6368]">Posts This Month</p>
              <p className="text-xl font-semibold text-[#202124]">
                {usage?.month_posts || 0} / {currentPlanLimits.month}
              </p>
            </div>
          </div>
        </div>
        
        <div className="dashboard-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#E6F4EA] rounded-lg flex items-center justify-center">
              <Eye className="w-5 h-5 text-[#1E8E3E]" />
            </div>
            <div>
              <p className="text-sm text-[#5F6368]">Total Views</p>
              <p className="text-xl font-semibold text-[#202124]">
                {gmbProfile.recentPosts.reduce((acc, p) => acc + p.views, 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FEF7E0] rounded-lg flex items-center justify-center">
              <MousePointer className="w-5 h-5 text-[#F9AB00]" />
            </div>
            <div>
              <p className="text-sm text-[#5F6368]">Total Clicks</p>
              <p className="text-xl font-semibold text-[#202124]">
                {gmbProfile.recentPosts.reduce((acc, p) => acc + p.clicks, 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#F3E8FD] rounded-lg flex items-center justify-center">
              <Star className="w-5 h-5 text-[#9334E9]" />
            </div>
            <div>
              <p className="text-sm text-[#5F6368]">Reviews</p>
              <p className="text-xl font-semibold text-[#202124]">{gmbProfile.reviewCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-2 space-y-6">
          {/* Action Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link to="/dashboard/post-generator" className="dashboard-card group" data-testid="quick-create-post">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#E8F0FE] rounded-lg flex items-center justify-center group-hover:bg-[#1A73E8] transition-colors">
                  <Plus className="w-6 h-6 text-[#1A73E8] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-medium text-[#202124]">Create Post</h3>
                  <p className="text-sm text-[#5F6368]">AI-powered</p>
                </div>
              </div>
            </Link>

            <Link to="/dashboard/review-reply" className="dashboard-card group" data-testid="quick-reply">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#E6F4EA] rounded-lg flex items-center justify-center group-hover:bg-[#1E8E3E] transition-colors">
                  <MessageSquare className="w-6 h-6 text-[#1E8E3E] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-medium text-[#202124]">Reply Reviews</h3>
                  <p className="text-sm text-[#5F6368]">{gmbProfile.reviewCount} reviews</p>
                </div>
              </div>
            </Link>

            <Link to="/dashboard/audit" className="dashboard-card group" data-testid="quick-audit">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#FEF7E0] rounded-lg flex items-center justify-center group-hover:bg-[#F9AB00] transition-colors">
                  <BarChart3 className="w-6 h-6 text-[#F9AB00] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-medium text-[#202124]">Full Audit</h3>
                  <p className="text-sm text-[#5F6368]">Score: {gmbProfile.auditScore}</p>
                </div>
              </div>
            </Link>
          </div>

          {/* Recent Posts - GMB Preview Style */}
          <div className="dashboard-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
                Recent Posts Performance
              </h3>
              <Link to="/dashboard/schedule" className="text-sm text-[#1A73E8] font-medium hover:underline">
                View All
              </Link>
            </div>

            <div className="space-y-4">
              {gmbProfile.recentPosts.map((post, i) => (
                <div key={post.id} className="flex gap-4 p-4 bg-[#F8F9FA] rounded-lg hover:bg-[#F1F3F4] transition-colors">
                  <img 
                    src={post.image} 
                    alt="Post"
                    className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[#202124] line-clamp-2">{post.content}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-[#5F6368]">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(post.date).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {post.views.toLocaleString()} views
                      </span>
                      <span className="flex items-center gap-1">
                        <MousePointer className="w-4 h-4" />
                        {post.clicks} clicks
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Issues & Suggestions */}
          <div className="dashboard-card">
            <h3 className="text-lg font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>
              Optimization Tips
            </h3>
            <div className="space-y-3">
              {gmbProfile.auditIssues.slice(0, 3).map((issue, i) => (
                <div 
                  key={i} 
                  className={`p-3 rounded-lg border ${
                    issue.severity === 'high' ? 'bg-[#FCE8E6] border-[#F5C6CB]' :
                    issue.severity === 'medium' ? 'bg-[#FEF7E0] border-[#FFE69C]' :
                    'bg-[#E8F0FE] border-[#B6D4FE]'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      issue.severity === 'high' ? 'text-[#D93025]' :
                      issue.severity === 'medium' ? 'text-[#F9AB00]' :
                      'text-[#1A73E8]'
                    }`} />
                    <div>
                      <p className="text-sm font-medium text-[#202124]">{issue.title}</p>
                      <p className="text-xs text-[#5F6368] mt-1">{issue.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Link 
              to="/dashboard/audit"
              className="mt-4 w-full btn-outline text-center block text-sm"
            >
              View All Recommendations
            </Link>
          </div>

          {/* Top Performing Content */}
          <div className="dashboard-card">
            <h3 className="text-lg font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>
              Best Performing Content
            </h3>
            <div className="space-y-3">
              {gmbProfile.topPerformingContent.map((item, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-[#F8F9FA] rounded-lg">
                  <div>
                    <p className="font-medium text-[#202124] text-sm">{item.type}</p>
                    <p className="text-xs text-[#5F6368]">{item.avgViews.toLocaleString()} avg views</p>
                  </div>
                  <div className="w-16 h-2 bg-[#DADCE0] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#1A73E8] rounded-full"
                      style={{ width: `${(item.avgViews / 2000) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-[#E8F0FE] rounded-lg">
              <div className="flex items-start gap-2">
                <Lightbulb className="w-4 h-4 text-[#1A73E8] mt-0.5" />
                <p className="text-xs text-[#202124]">
                  <strong>Pro Tip:</strong> {gmbProfile.topPerformingContent[0]?.suggestion}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade CTA for Free Users */}
      {user?.membership_plan === 'free' && (
        <div className="dashboard-card bg-gradient-to-r from-[#1A73E8] to-[#1557B0] text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>
                Unlock More Features
              </h3>
              <p className="mt-1 text-white/80">
                Get auto-posting, unlimited posts, and priority support.
              </p>
            </div>
            <Link
              to="/pricing"
              className="bg-white text-[#1A73E8] font-semibold px-6 py-3 rounded-lg hover:bg-[#F8F9FA] transition-colors"
              data-testid="upgrade-cta"
            >
              Upgrade Now
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardHome;
